import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useActorTableChangeVersion } from "@-/dev.improve.haniverse.lib.database.change.actor";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";

/** Graphemes with a pronunciation card in study, plus each enrolled `(grapheme, pinyin)` reading. */
export type PronouncedGraphemes = {
  graphemes: ReadonlySet<string>;
  /** Keys are `${grapheme}\0${pinyin}` (tone-number pinyin). */
  readings: ReadonlySet<string>;
};

export function pronouncedReadingKey({
  grapheme,
  pinyin,
}: {
  grapheme: string;
  pinyin: string;
}): string {
  return `${grapheme}\0${pinyin}`;
}

// Derived from `user_card` + its pronunciation-card relation, so the key is invalidated when
// either changes. The predicate is derived from the read's `with`.
const PRONOUNCED_GRAPHEMES_CONFIG = {
  columns: {},
  where: { fsrsCardId: { isNotNull: true } },
  with: { userGraphemePronunciationCard: { columns: { grapheme: true, pinyin: true } } },
} as const;

export function useGetPronouncedGraphemesQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (context) => context.db);
  const withTableChangeVersion = useActorTableChangeVersion(
    "userCard",
    PRONOUNCED_GRAPHEMES_CONFIG,
  );
  return () => withTableChangeVersion(["pronouncedGraphemes", { actorDb }] as const);
}

/**
 * The graphemes with at least one pronunciation card in study: the counterpart of the studied
 * (meaning) set for pronunciation-level lessons, which introduce a meaning-only level's readings.
 * `readings` lists each enrolled pronunciation card so Level 2-P1 can offer 干 twice (gan4, then
 * gan1) without treating the first enrollment as completing both slots.
 */
export const [usePronouncedGraphemesQueryOptions, pronouncedGraphemesQueryFn] =
  createUseQueryOptions(
    useGetPronouncedGraphemesQueryKey,
    async ({ queryKey: [_, { actorDb }] }) => {
      // Soft-delete filter is injected by the wrapped `actorDb` query proxy.
      const studyCards = await actorDb(import.meta.location).query.userCard.findMany(
        PRONOUNCED_GRAPHEMES_CONFIG,
      );
      const graphemes = new Set<string>();
      const readings = new Set<string>();
      for (const studyCard of studyCards) {
        const link = studyCard.userGraphemePronunciationCard;
        if (link === undefined || link === null) {
          continue;
        }
        graphemes.add(link.grapheme);
        readings.add(pronouncedReadingKey({ grapheme: link.grapheme, pinyin: link.pinyin }));
      }
      return { graphemes, readings } satisfies PronouncedGraphemes;
    },
  );
